import React, { useCallback, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    FloatingPortal,
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
    useMergeRefs,
} from '@floating-ui/react';
import { Plus, RotateCcw, Search } from 'lucide-react';
import Spinner from '../../ui/Spinner';
import TextInput from '../../ui/TextInput';
import { cx } from '../../ui/classNames';
import {
    CountryChipLabel,
    getCountryChipDisplay,
} from '../../shared/securityDisplayPrimitives';
import { historyCommandForKey, isMacPlatform } from '../spreadsheet/keyboardCommands';
import {
    EMPTY_PICKS,
    MAX_PICKS_PER_SAVE,
    PICK_OUTCOME,
    identifierTypeLabel,
} from './pendingAdditions';
import useAddSecuritySearch, {
    MAX_QUERY_CHARACTERS,
    WATCHLIST_SEARCH_API,
    normalizeQuery,
} from './useAddSecuritySearch';
import { statusTagClassName } from './addSecurityStyles';

const IS_MAC = isMacPlatform();
const SEARCHING = new Set(['debouncing', 'loading']);
const BLOCK_MESSAGES = Object.freeze({
    watchlist_full: 'Your Watchlist is full. Remove a security to add another.',
    save_first: `Save these ${MAX_PICKS_PER_SAVE} additions before adding more.`,
});
const UNRESOLVED_HINT = "Apply looks it up the same way an upload does. If it can't be matched, it waits for you in Security Resolution.";
const STATE_SUFFIX = Object.freeze({
    in_watchlist: 'already in your Watchlist',
    pending: 'already being added',
});
const SHOW_IT = 'Show it';

function isPendingPick(pick, pendingPicks) {
    return Boolean(pick) && pendingPicks.some((pending) => (
        pending.key === pick.key && pending.listingKey === pick.listingKey && !pending.stale
    ));
}

// Why nothing new can be picked: the Watchlist cap (the caller's count or the
// server's, whichever is tighter) or the one-Save limit. Null when free.
function capacityBlock({ remainingCapacity, capacity, pendingCount }) {
    if (pendingCount >= MAX_PICKS_PER_SAVE) return 'save_first';
    const remaining = [remainingCapacity];
    if (capacity) {
        remaining.push(capacity.max_securities - capacity.current_security_count - pendingCount);
    }
    return remaining.some((value) => Number.isFinite(value) && value <= 0) ? 'watchlist_full' : null;
}

// Every row of the list: the results in the server's order, then "Add as
// unresolved…" or "Retry search". A row's state decides what choosing it does;
// a security already in the Watchlist offers to show it when the owner can.
function buildOptions(search, { pendingPicks, block, canShow }) {
    const options = search.candidates.map((candidate) => {
        let state = 'addable';
        if (candidate.inWatchlist) state = 'in_watchlist';
        else if (!candidate.pick) state = 'unavailable';
        else if (isPendingPick(candidate.pick, pendingPicks)) state = 'pending';
        else if (block) state = 'blocked';
        return {
            type: 'candidate',
            key: `candidate:${candidate.key}`,
            candidate,
            state,
            block,
            showable: state === 'in_watchlist' && canShow,
        };
    });
    if (search.unresolved) {
        let state = 'addable';
        if (isPendingPick(search.unresolved.pick, pendingPicks)) state = 'pending';
        else if (block) state = 'blocked';
        options.push({ type: 'unresolved', key: 'unresolved', offer: search.unresolved, state, block });
    }
    if (search.status === 'error') options.push({ type: 'retry', key: 'retry', state: 'addable' });
    return options;
}

function isDisabled(option) {
    return option.state === 'unavailable' || option.state === 'blocked';
}

// The highlighted row: the one chosen with the keys or pointer, else the first
// row that can be chosen (MSI's preferred listing), else the first row.
function resolveActiveIndex(options, activeKey) {
    const chosen = options.findIndex((option) => option.key === activeKey);
    if (chosen >= 0) return chosen;
    const firstEnabled = options.findIndex((option) => !isDisabled(option));
    if (firstEnabled >= 0) return firstEnabled;
    return options.length ? 0 : -1;
}

function describeCandidate(candidate) {
    return [candidate.ticker, candidate.companyName, candidate.country, candidate.exchange]
        .filter(Boolean)
        .join(' · ');
}

function optionLabel(option) {
    if (option.type === 'retry') return 'Retry search';
    const base = option.type === 'unresolved' ? option.offer.label : describeCandidate(option.candidate);
    let detail = STATE_SUFFIX[option.state] || '';
    if (option.state === 'unavailable') detail = option.candidate.unavailableReason;
    if (option.state === 'blocked') detail = BLOCK_MESSAGES[option.block];
    if (option.showable) detail = `${detail}, ${SHOW_IT}`;
    return detail ? `${base}, ${detail}` : base;
}

function identifierText(search) {
    return search.identifier
        ? `${identifierTypeLabel(search.identifier.type)} ${search.identifier.value}`
        : search.query;
}

function statusMessage(search) {
    switch (search.status) {
        case 'minimum':
            return 'Type at least 2 characters of a ticker, ISIN or company.';
        case 'debouncing':
        case 'loading':
            return search.mode === 'identifier' ? `Looking up ${search.query}…` : 'Searching…';
        case 'empty':
            return search.mode === 'identifier'
                ? `MSI has no listing for ${identifierText(search)} yet.`
                : `No matches for "${search.query}".`;
        case 'error':
            return search.message;
        case 'success':
            return search.mode === 'identifier' ? `Listings for ${identifierText(search)}` : '';
        default:
            return '';
    }
}

function liveStatus(search) {
    if (search.status === 'success') {
        const count = search.candidates.length;
        const lead = search.mode === 'identifier' ? `${statusMessage(search)}: ` : '';
        return `${lead}${count} ${count === 1 ? 'result' : 'results'}.`;
    }
    return ['empty', 'error', 'minimum'].includes(search.status) ? statusMessage(search) : '';
}

function existingMessage(name, onExisting) {
    return `${name} is already in your Watchlist.${onExisting ? ' Showing it.' : ''}`;
}

function securityOf(source) {
    return {
        ticker: source.ticker,
        companyName: source.companyName,
        country: source.country,
        exchange: source.exchange,
        ...(source.cogniveId ? { cogniveId: source.cogniveId } : {}),
    };
}

// A quiet dot between the parts (MUI's disabled text colour).
const separator = (
    <span aria-hidden="true" className="shrink-0 text-black/[0.38] dark:text-white/50">·</span>
);
const SPACER = <span className="flex-[1_0_8px]" />;
// Icons in the list: 16px in the secondary text colour.
const OPTION_ICON_CLASS_NAME = 'shrink-0 text-content-secondary';

function CandidateContent({ option }) {
    const { candidate, state } = option;
    return (
        <>
            <span className="shrink-0 font-bold">{candidate.ticker}</span>
            {candidate.companyName && (
                <>
                    {separator}
                    <span className="min-w-0 flex-[0_1_auto] overflow-hidden text-ellipsis">
                        {candidate.companyName}
                    </span>
                </>
            )}
            {candidate.country && (
                <>
                    {separator}
                    <span className="shrink-0">
                        <CountryChipLabel display={getCountryChipDisplay(candidate.country)} />
                    </span>
                </>
            )}
            {candidate.exchange && (
                <>
                    {separator}
                    <span className="min-w-0 flex-[0_2_auto] overflow-hidden text-ellipsis text-content-secondary">
                        {candidate.exchange}
                    </span>
                </>
            )}
            {SPACER}
            {state === 'in_watchlist' && (
                <span className={statusTagClassName('info')}>Already in your Watchlist</span>
            )}
            {option.showable && (
                <span className="shrink-0 text-[12px] font-semibold text-accent underline">
                    {SHOW_IT}
                </span>
            )}
            {state === 'pending' && <span className={statusTagClassName('warning')}>Adding</span>}
            {state === 'unavailable' && (
                <span className="shrink-0 text-[12px] text-content-secondary">
                    {candidate.unavailableReason}
                </span>
            )}
        </>
    );
}

function OptionContent({ option }) {
    if (option.type === 'candidate') return <CandidateContent option={option} />;
    if (option.type === 'retry') {
        return (
            <>
                <RotateCcw aria-hidden="true" size={16} className={OPTION_ICON_CLASS_NAME} />
                <span className="font-semibold">Retry search</span>
            </>
        );
    }
    return (
        <>
            <Plus aria-hidden="true" size={16} className={cx(OPTION_ICON_CLASS_NAME, 'mt-[3px] self-start')} />
            <span className="flex min-w-0 flex-col whitespace-normal">
                <span className="font-semibold">{option.offer.label}</span>
                <span className="text-[12px] leading-[1.35] text-content-secondary">
                    {UNRESOLVED_HINT}
                </span>
            </span>
            {SPACER}
            {option.state === 'pending' && <span className={statusTagClassName('warning')}>Adding</span>}
        </>
    );
}

// The grid cell editor's field (the shared cell-editor look: a 6px radius in
// the divider colour, an accent border and soft accent ring while focused)
// in 13px regular type, its search icon and spinner 4px from the text and
// 6px from the edge, border included.
const FIELD_CLASS_NAME = cx(
    '!gap-1 !rounded-md !border-hairline !px-[5px] !text-[13px] !font-normal hover:!border-hairline',
    'has-[:focus]:!border-accent',
    'has-[:focus]:!shadow-[0_0_0_2px_rgb(var(--c-accent)/0.16)] [&_svg]:block',
);
// The results paper below the box: the app's paper (no shadow), as wide as
// it can be on a phone, 460px from MUI's small breakpoint up.
const POPUP_CLASS_NAME = cx(
    'z-[1300] box-border w-[calc(100vw-16px)] max-w-[calc(100vw-16px)] overflow-hidden rounded-lg border border-solid',
    'border-ui-line bg-ui-surface text-[13px] text-ui-ink min-[600px]:w-[460px]',
);
const HEADER_CLASS_NAME = 'flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-content-secondary';
const LIST_CLASS_NAME = 'm-0 max-h-[320px] list-none overflow-y-auto px-0 py-1';
// The list is ruled off from a status line above it.
const LIST_BELOW_HEADER_CLASS_NAME = 'border-0 border-t border-solid border-hairline';
const NOTE_CLASS_NAME = 'border-0 border-t border-solid border-hairline px-2.5 py-1.5 text-[12px] text-content-secondary';
// A row of the list: MUI's selected tint while highlighted, its hover tint
// under a pointer; a row that cannot be chosen is secondary text.
const OPTION_CLASS_NAME = cx(
    'flex min-h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap px-2.5 py-1',
    '[-webkit-tap-highlight-color:transparent] aria-selected:bg-black/[0.08] dark:aria-selected:bg-white/[0.16]',
    'aria-disabled:cursor-default aria-disabled:text-content-secondary',
    '[@media(hover:hover)]:[&:hover:not([aria-selected=true])]:bg-black/[0.04]',
    'dark:[@media(hover:hover)]:[&:hover:not([aria-selected=true])]:bg-white/[0.08]',
);

/**
 * The search box an "+ Add security" row's identity cell turns into: a
 * combobox whose list shows "AAPL · Apple Inc. · USA · NASDAQ" rows in MSI's
 * order (preferred listing first). ↑/↓ move, Enter or a click/tap picks,
 * Escape cancels. Enter before the results arrive searches at once and picks
 * the top result if it is an exact ticker match.
 *
 * A pick calls `onPick(pick)`; return the PICK_OUTCOME from addPick so the
 * editor can say what happened (nothing returned counts as added). After an
 * addition the box clears for the next entry. A security the server reports
 * as already in the Watchlist is "Already in your Watchlist"; with
 * `onExisting`, choosing it shows it ("Show it": `onExisting(security)`).
 * When a search finds nothing, "Add as unresolved…" looks the text up the way
 * the dialog's "Add by identifier" does. `onCancel({ reason })` follows Escape
 * ('escape') or leaving an empty box ('blur'). In an empty box, Mod+Z and
 * Mod+Shift+Z (Ctrl+Y) call `onUndo` / `onRedo`, the owner's Undo and Redo.
 * `searchApi` replaces the backend search (see useAddSecuritySearch).
 */
export default function AddSecurityRowEditor({
    onPick,
    onCancel,
    onExisting = null,
    onCapacityChange = null,
    onUndo = null,
    onRedo = null,
    pendingPicks = EMPTY_PICKS,
    remainingCapacity = null,
    initialQuery = '',
    autoFocus = true,
    disabled = false,
    searchApi = WATCHLIST_SEARCH_API,
}) {
    const search = useAddSecuritySearch({ initialQuery, onCapacity: onCapacityChange, searchApi });
    const baseId = useId();
    const listboxId = `${baseId}-listbox`;
    const optionId = (index) => `${baseId}-option-${index}`;
    // After the next layout (a new pending row, or the box just mounted).
    const revealBox = () => window.requestAnimationFrame(() => {
        rootRef.current?.scrollIntoView?.({ block: 'nearest' });
    });
    const [focused, setFocused] = useState(false);
    const [activeKey, setActiveKey] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const startedRef = useRef(false);

    const block = capacityBlock({
        remainingCapacity,
        capacity: search.capacity,
        pendingCount: pendingPicks.length,
    });
    const canShow = typeof onExisting === 'function';
    const options = buildOptions(search, { pendingPicks, block, canShow });
    const activeIndex = resolveActiveIndex(options, activeKey);
    const activeOption = activeIndex >= 0 ? options[activeIndex] : null;
    const popupOpen = focused && !disabled && search.status !== 'idle';
    const listboxShown = popupOpen && options.length > 0;
    // The results below the box, kept on screen.
    const { refs, floatingStyles } = useFloating({
        open: popupOpen,
        placement: 'bottom-start',
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [offset(4), flip(), shift({ padding: 8 })],
    });
    // The box's row-high root, kept in view as the grid grows above it.
    const rootRef = useRef(null);
    const setRoot = useMergeRefs([rootRef, refs.setReference]);
    const searching = SEARCHING.has(search.status);
    const header = statusMessage(search);
    const note = block ? BLOCK_MESSAGES[block] : (search.status !== 'error' && search.message) || '';
    const liveText = announcement || (popupOpen ? liveStatus(search) : '');

    // Handlers that finish after an await read the newest props from here.
    const latestRef = useRef(null);
    latestRef.current = { onPick, onExisting, pendingPicks, remainingCapacity };

    // Unmounting (the row scrolled away, the grid closed it) stops the search.
    const { cancel: cancelSearch } = search;
    const inputRef = useCallback((node) => {
        if (node === null) cancelSearch();
    }, [cancelSearch]);

    const commitPick = (pick) => {
        const latest = latestRef.current;
        const outcome = latest.onPick(pick) || PICK_OUTCOME.ADDED;
        switch (outcome) {
            case PICK_OUTCOME.ADDED:
            case PICK_OUTCOME.REPLACED:
                setActiveKey(null);
                search.reset();
                setAnnouncement(outcome === PICK_OUTCOME.REPLACED
                    ? `Adding ${pick.label} (${pick.country}) instead.`
                    : `Adding ${pick.label} — type the next ticker, ISIN or company.`);
                // The new pending row above pushes the box down: keep it in view.
                revealBox();
                return;
            case PICK_OUTCOME.ALREADY_IN_WATCHLIST:
                setActiveKey(null);
                search.reset();
                setAnnouncement(existingMessage(pick.label, latest.onExisting));
                latest.onExisting?.(securityOf(pick));
                return;
            case PICK_OUTCOME.DUPLICATE:
                setAnnouncement(`${pick.label} is already being added.`);
                return;
            case PICK_OUTCOME.WATCHLIST_FULL:
            case PICK_OUTCOME.SAVE_FIRST:
                setAnnouncement(BLOCK_MESSAGES[outcome]);
                return;
            default:
                setAnnouncement('That choice is no longer available. Search for it again.');
        }
    };

    const lookUp = async () => {
        setAnnouncement('');
        const result = await search.lookupIdentifier();
        if (result?.type === 'unresolved') commitPick(result.pick);
    };

    const choose = (option) => {
        if (!option) return;
        if (option.type === 'retry') {
            setAnnouncement('');
            void search.retry();
            return;
        }
        if (option.state === 'blocked') {
            setAnnouncement(BLOCK_MESSAGES[option.block]);
            return;
        }
        if (option.type === 'unresolved') {
            if (option.state === 'pending') {
                setAnnouncement(`${option.offer.pick.label} is already being added.`);
            } else if (option.offer.pick) {
                commitPick(option.offer.pick);
            } else {
                void lookUp();
            }
            return;
        }
        const { candidate } = option;
        switch (option.state) {
            case 'in_watchlist':
                setActiveKey(null);
                search.reset();
                setAnnouncement(existingMessage(
                    `${candidate.ticker} (${candidate.country})`,
                    latestRef.current.onExisting,
                ));
                latestRef.current.onExisting?.(securityOf(candidate));
                return;
            case 'pending':
                setAnnouncement(`${candidate.pick.label} is already being added.`);
                return;
            case 'unavailable':
                setAnnouncement(candidate.unavailableReason);
                return;
            default:
                commitPick(candidate.pick);
        }
    };

    // Enter before the results: search now, then take the top result only when
    // it is an exact ticker match; otherwise the results wait for a choice.
    const chooseWhenSettled = async () => {
        const settled = await search.flush();
        const top = settled?.candidates[0];
        if (!top?.exactTicker) return;
        const latest = latestRef.current;
        const [first] = buildOptions(settled, {
            pendingPicks: latest.pendingPicks,
            block: capacityBlock({
                remainingCapacity: latest.remainingCapacity,
                capacity: settled.capacity,
                pendingCount: latest.pendingPicks.length,
            }),
            canShow: typeof latest.onExisting === 'function',
        });
        choose(first);
    };

    const moveActive = (delta) => {
        const next = (activeIndex + delta + options.length) % options.length;
        setActiveKey(options[next].key);
        document.getElementById(optionId(next))?.scrollIntoView?.({ block: 'nearest' });
    };

    const handleKeyDown = (event) => {
        if (event.nativeEvent?.isComposing) return;
        const history = historyCommandForKey(event, { isMac: IS_MAC });
        if (history) {
            // An empty box has no text to undo: the keys undo the last change.
            const step = history === 'undo' ? onUndo : onRedo;
            if (step && !search.input) {
                event.preventDefault();
                step();
            }
            return;
        }
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                if (!listboxShown) return;
                event.preventDefault();
                moveActive(event.key === 'ArrowDown' ? 1 : -1);
                return;
            case 'Enter':
                event.preventDefault();
                event.stopPropagation();
                if (searching) void chooseWhenSettled();
                else if (listboxShown) choose(activeOption);
                return;
            case 'Escape':
                event.preventDefault();
                event.stopPropagation();
                search.cancel();
                onCancel({ reason: 'escape' });
                return;
            default:
        }
    };

    const handleFocus = (event) => {
        setFocused(true);
        if (startedRef.current) return;
        startedRef.current = true;
        // Opened from the keyboard, the row may still be below the fold.
        revealBox();
        if (!initialQuery) return;
        // Keep typing after the character that opened the editor.
        const end = event.target.value.length;
        event.target.setSelectionRange?.(end, end);
        search.setInput(initialQuery);
    };

    const handleBlur = () => {
        setFocused(false);
        if (normalizeQuery(search.input)) return;
        search.cancel();
        onCancel({ reason: 'blur' });
    };

    return (
        <div ref={setRoot} className="relative flex h-full w-full min-w-0 items-center">
            <TextInput
                ref={inputRef}
                role="combobox"
                aria-label="Add security"
                aria-autocomplete="list"
                aria-expanded={listboxShown}
                aria-controls={listboxShown ? listboxId : undefined}
                aria-activedescendant={listboxShown && activeOption ? optionId(activeIndex) : undefined}
                value={search.input}
                onChange={(event) => {
                    setActiveKey(null);
                    setAnnouncement('');
                    search.setInput(event.target.value);
                }}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoFocus={autoFocus}
                disabled={disabled}
                placeholder="Type a ticker, ISIN or company"
                maxLength={MAX_QUERY_CHARACTERS}
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                startAdornment={<Search aria-hidden="true" size={16} />}
                endAdornment={searching ? <Spinner size={14} aria-hidden="true" className="text-accent" /> : null}
                className={FIELD_CLASS_NAME}
            />
            <span role="status" aria-live="polite" className="sr-only">
                {liveText}
            </span>
            {popupOpen && (
                <FloatingPortal>
                    {/* Pressing anywhere in the list keeps focus in the search box. */}
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        data-add-security-results=""
                        className={POPUP_CLASS_NAME}
                        onMouseDown={(event) => event.preventDefault()}
                    >
                        {header && (
                            <div className={HEADER_CLASS_NAME}>
                                {searching && <Spinner size={12} aria-hidden="true" className="text-accent" />}
                                <span>{header}</span>
                            </div>
                        )}
                        {listboxShown && (
                            <ul
                                role="listbox"
                                id={listboxId}
                                aria-label="Securities to add"
                                className={cx(LIST_CLASS_NAME, header && LIST_BELOW_HEADER_CLASS_NAME)}
                            >
                                {options.map((option, index) => (
                                    <li
                                        key={option.key}
                                        id={optionId(index)}
                                        role="option"
                                        aria-selected={index === activeIndex}
                                        aria-disabled={isDisabled(option) || undefined}
                                        aria-label={optionLabel(option)}
                                        onMouseMove={() => {
                                            if (index !== activeIndex) setActiveKey(option.key);
                                        }}
                                        onClick={() => choose(option)}
                                        className={OPTION_CLASS_NAME}
                                    >
                                        <OptionContent option={option} />
                                    </li>
                                ))}
                            </ul>
                        )}
                        {note && <div className={NOTE_CLASS_NAME}>{note}</div>}
                    </div>
                </FloatingPortal>
            )}
        </div>
    );
}

AddSecurityRowEditor.propTypes = {
    onPick: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onExisting: PropTypes.func,
    onCapacityChange: PropTypes.func,
    onUndo: PropTypes.func,
    onRedo: PropTypes.func,
    pendingPicks: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        listingKey: PropTypes.string.isRequired,
        stale: PropTypes.bool,
    })),
    remainingCapacity: PropTypes.number,
    initialQuery: PropTypes.string,
    autoFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    searchApi: PropTypes.shape({
        searchWatchlistSecurityDiscovery: PropTypes.func.isRequired,
        previewSecurityResolutionAdd: PropTypes.func.isRequired,
    }),
};
